import { Keys } from 'chessboard/plugins/keys/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import { createKeysService } from 'chessboard/plugins/keys/service/keys.service';
import { Core } from 'core/config/types';
import { is } from 'utils/app/model/is';
import { createCoreEvent } from 'chessboard/core/model/create-core-event';

/**
 * Creates a Keys Plugin that allows adding listeners to keys.
 */
export function createKeysPlugin(): Plugins.Model {
  let run: Core.Run;

  const keys = createKeysService({
    onKeydown: createEmitter(Keys.Events.Keydown),
    onKeyup: createEmitter(Keys.Events.Keyup),
  });

  const callbacks = {
    [Keys.Events.Keydown]: {},
    [Keys.Events.Keyup]: {},
  };

  const keysAPI: Keys.API = {
    isPressed: keys.isPressed,
    getPressedKeys: keys.getPressedKeys,
    setOverrides,
    removeOverrides,
  };

  return {
    api: () => keysAPI,
    create,
    destroy,
    match: [
      {
        condition: is(Core.OptionsEvents.Update),
        handler: updateKeysPlugin,
      },
      {
        condition: is(Keys.Events.Keydown),
        handler: queueKeyCallback,
      },
      {
        condition: is(Keys.Events.Keyup),
        handler: queueKeyCallback,
      },
    ],
    name: Keys.Plugin.Name,
  };

  /**
   * Create a reference to the context run function.
   */
  function create({ run: ctxRun, options }: Core.Context) {
    run = ctxRun;

    toggleKeysService(options.enabled);
  }

  /**
   * Destroy the keys service.
   */
  function destroy() {
    keys.destroy();
  }

  /**
   * Create key event emitter which will fire from the keys service.
   */
  function createEmitter(trigger: Keys.Events) {
    return function emit(key: string, event) {
      run?.(createCoreEvent(trigger, { key, rawEvent: event }));
    };
  }

  /**
   * Queues a key event callback for specified event trigger.
   */
  function queueKeyCallback(event: Core.Event, context: Core.Context): void {
    const { type: trigger, data } = event;
    const callback = callbacks[trigger][data.key];

    if (!callback) {
      return;
    }

    context.queue.add(callback(context.api));
  }

  /**
   * Override current key event callbacks.
   */
  function setOverrides(overrides: Keys.Override[]): void {
    overrides.forEach(({ key, callback, trigger = Keys.Events.Keydown }) => {
      callbacks[trigger][key] = callback;
    });
  }

  /**
   * Remove current key event callbacks.
   */
  function removeOverrides(overrides: Keys.Override[]): void {
    overrides.forEach(({ trigger = Keys.Events.Keydown, key }) => {
      delete callbacks[trigger][key];
    });
  }

  /**
   * Keys plugin event handler, for disabling/enabling
   */
  function updateKeysPlugin(_event: Core.Event, ctx: Core.Context): void {
    const { options } = ctx;
    const isEnabled = options.captureKeyStrokes && options.enabled;

    toggleKeysService(isEnabled);
  }

  /**
   * Toggle keys services
   */
  function toggleKeysService(enabled: boolean) {
    if (enabled) {
      keys.enable();
    } else {
      keys.disable();
    }
  }
}
